import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, ToastRegion, useToast } from '../ui';
import PublicTopBar, { PublicFooter } from './PublicTopBar';
import ExampleUnit from './ExampleUnit';
import ThemeToggle from '../AppShell/ThemeToggle';
import { demoContactEmail } from '../../config/contact';
import './PublicV2.css';


function SignInButton({ onSignIn, isLoading, variant }) {
    return (
        <Button variant={variant} onClick={onSignIn} disabled={isLoading}>
            {isLoading ? 'Signing in…' : 'Sign in'}
        </Button>
    );
}
SignInButton.propTypes = { onSignIn: PropTypes.func.isRequired, isLoading: PropTypes.bool, variant: PropTypes.string };

const prefersReducedMotion = () => {
    try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) { return false; }
};

/**
 * A link to a section of this page. It scrolls in place and leaves the URL
 * alone: a hash change would be a new history entry without the
 * "stay on landing" state, and `/` could bounce back to a saved sign-in
 * (QA L-13). The real `href="#…"` keeps it working without JavaScript.
 */
function InPageLink({ to, className, children }) {
    const onClick = (e) => {
        const target = document.getElementById(to);
        if (!target) return;
        e.preventDefault();
        if (typeof target.scrollIntoView === 'function') {
            target.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
        }
    };
    return <a className={className} href={`#${to}`} onClick={onClick}>{children}</a>;
}
InPageLink.propTypes = { to: PropTypes.string.isRequired, className: PropTypes.string, children: PropTypes.node };

const FIXES = [
    ['Aged units eat floor plan.', 'See which units are past 90 or 180 days and what they cost you to hold.'],
    ['Pricing on gut, not comps.', 'Check every asking price against market comps and a model estimate.'],
    ['Turn varies by branch.', 'Compare categories and branches side by side.'],
];

// Small CSS drawings for the tiles (decoration; the tile text says it).
const MINI = {
    price: (
        <div className="pub2-mini pub2-mini--price" aria-hidden="true">
            <div className="pub2-scale">
                <span className="pub2-scale-range" style={{ '--from': '25%', '--to': '75%' }} />
                <span className="pub2-scale-mark pub2-scale-mark--ai" style={{ '--at': '50%' }} />
                <span className="pub2-scale-mark pub2-scale-mark--ask" style={{ '--at': '83%' }} />
            </div>
        </div>
    ),
    risk: (
        <div className="pub2-mini" aria-hidden="true">
            <div className="pub2-meter"><span /><span className="is-on is-amber" /><span /></div>
        </div>
    ),
    cost: (
        <div className="pub2-mini" aria-hidden="true">
            <div className="pub2-stack">
                <span className="pub2-stack-cost" style={{ '--w': '58%' }} />
                <span className="pub2-stack-recon" style={{ '--w': '12%' }} />
                <span className="pub2-stack-carry" style={{ '--w': '10%' }} />
                <span className="pub2-stack-margin" style={{ '--w': '20%' }} />
            </div>
        </div>
    ),
    interest: (
        <div className="pub2-mini" aria-hidden="true">
            <div className="pub2-ex-bars pub2-ex-bars--mini">
                {[35, 50, 45, 70, 60, 85].map((h) => <span key={h} style={{ '--h': `${h}%` }} />)}
            </div>
        </div>
    ),
    ask: (
        <div className="pub2-mini pub2-mini--ask" aria-hidden="true">
            <span className="pub2-brief-chip">Pre-call brief</span>
            <span className="pub2-brief-line" style={{ '--w': '92%' }} />
            <span className="pub2-brief-line" style={{ '--w': '78%' }} />
            <span className="pub2-brief-line" style={{ '--w': '64%' }} />
        </div>
    ),
};

const TILES = [
    ['price', 'Price position', 'Asking vs. AI estimate and range.'],
    ['risk', 'Days-to-sell risk', 'Likely time to sell and risk tier.'],
    ['cost', 'True cost and margin', 'What the unit really cost and what’s left.'],
    ['interest', 'Buyer interest', 'Views, email leads and open quotes.'],
];

const HOW = [
    'Built on your dealership’s inventory and sales history, plus listing data.',
    'Each dealership’s data is kept separate.',
    'The estimate is a model, not an appraisal: you see a likely range, and the comps behind it when the market has them.',
    'Your team signs in through your own dealership address.',
];

function DemoContact({ email }) {
    const { toast, showToast, dismissToast } = useToast();
    const [failed, setFailed] = useState(false);
    const copy = async () => {
        setFailed(false);
        try {
            await navigator.clipboard.writeText(email);
            showToast({ text: 'Copied' });
        } catch (e) {
            setFailed(true);
        }
    };
    return (
        <section id="demo" className="pub2-section pub2-demo" aria-labelledby="pub2-demo-h">
            <div className="pub2-demo-text">
                <h2 className="pub2-h2" id="pub2-demo-h">See IronSight on your own inventory.</h2>
                <p className="pub2-text">We’ll walk you through it with sample data first.</p>
            </div>
            <div className="pub2-card pub2-demo-card">
                <span className="pub2-eyebrow">Contact</span>
                <b className="pub2-demo-email">{email}</b>
                <Button onClick={copy}>Copy email address</Button>
                {/* Always in the DOM so the message is announced when it fills (QA L-9). */}
                <p className="pub2-text pub2-copy-status" role="status">
                    {failed ? 'Couldn’t copy. Select the address above to copy it.' : ''}
                </p>
            </div>
            <ToastRegion toast={toast} onDismiss={dismissToast} />
        </section>
    );
}
DemoContact.propTypes = { email: PropTypes.string.isRequired };

/**
 * The public landing page in the new design (?ui=v2), built from the
 * approved wireframe: sign-in first (most visitors are invited and want in),
 * then one example unit explained, what it fixes, what you see per unit,
 * how it works and how data is handled, and the demo contact when one is
 * configured. Sign in → onSignIn (App sends it to /login), as today.
 */
export default function LandingPageV2({ onSignIn, isLoading = false, error = null }) {
    const demoEmail = demoContactEmail();
    return (
        <div className="pub2 pub2-landing">
            <PublicTopBar showTheme={false}>
                <InPageLink className="v2-btn v2-btn--ghost pub2-help" to="how">Help</InPageLink>
                {/* The app's theme menu (config/theme.js, same storage key), so
                    the choice made here carries into sign-in and the app. */}
                <ThemeToggle />
                <SignInButton variant="ghost" onSignIn={onSignIn} isLoading={isLoading} />
            </PublicTopBar>

            <main className="pub2-main">
                {/* DOM order = reading and tab order on a phone (QA L-4):
                    eyebrow, H1, sub-headline, sign-in card, demo link. On wide
                    screens the grid places the card in the right column. */}
                <section className="pub2-hero" aria-label="IronSight">
                    <span className="pub2-eyebrow pub2-hero-eyebrow">Used-equipment intelligence for ag and construction dealers</span>
                    <h1 className="pub2-h1">Know which units to reprice, hold or wholesale, before they age.</h1>
                    <p className="pub2-sub">
                        IronSight puts an AI price estimate and likely range next to every used
                        unit’s asking price, flags units at risk of sitting, and shows market comps
                        and buyer interest in one view, for every branch.
                    </p>
                    <section className="pub2-card pub2-signin" aria-labelledby="pub2-signin-h">
                        <h2 className="pub2-h3 pub2-signin-h" id="pub2-signin-h">Sign in to your dealership</h2>
                        <SignInButton variant="primary" onSignIn={onSignIn} isLoading={isLoading} />
                        {error && <p className="pub2-error" role="alert">Sign-in didn’t go through. Please try again.</p>}
                        <p className="pub2-hint">
                            Use your dealership’s sign-in address from your invitation email. Access is
                            by invitation from your dealership’s IronSight admin.
                        </p>
                    </section>
                    {demoEmail && (
                        <p className="pub2-demo-link">
                            Not using IronSight yet? <InPageLink to="demo">Request a demo ↓</InPageLink>
                        </p>
                    )}
                </section>

                <div className="pub2-showcase">
                    <ExampleUnit />
                </div>

                <section className="pub2-section" aria-labelledby="pub2-fix-h">
                    <h2 className="pub2-h2" id="pub2-fix-h">What it fixes on the lot</h2>
                    <div className="pub2-fixes">
                        {FIXES.map(([h, p], i) => (
                            <div className="pub2-fix" key={h}>
                                <span className="pub2-fix-n" aria-hidden="true">{i + 1}</span>
                                <div>
                                    <h3 className="pub2-h3">{h}</h3>
                                    <p className="pub2-text">{p}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                <section className="pub2-section pub2-section--alt" aria-labelledby="pub2-see-h">
                    <div className="pub2-section-in">
                        <h2 className="pub2-h2" id="pub2-see-h">What you see for every unit</h2>
                        <div className="pub2-tiles">
                            {TILES.map(([key, h, p]) => (
                                <article className="pub2-card pub2-tile" key={key}>
                                    {MINI[key]}
                                    <h3 className="pub2-h3">{h}</h3>
                                    <p className="pub2-text">{p}</p>
                                </article>
                            ))}
                            <article className="pub2-card pub2-tile pub2-tile--wide">
                                {MINI.ask}
                                <div className="pub2-tile-text">
                                    <h3 className="pub2-h3">Ask IronSight</h3>
                                    <p className="pub2-text">A pre-call brief for any unit, written from your own data.</p>
                                </div>
                            </article>
                        </div>
                    </div>
                </section>

                <section id="how" className="pub2-section" aria-labelledby="pub2-how-h">
                    <h2 className="pub2-h2" id="pub2-how-h">How it works, and how your data is handled</h2>
                    <ul className="pub2-how">
                        {HOW.map((t) => <li key={t}>{t}</li>)}
                    </ul>
                </section>

                {demoEmail && <DemoContact email={demoEmail} />}
            </main>

            <PublicFooter />
        </div>
    );
}

LandingPageV2.propTypes = {
    onSignIn: PropTypes.func.isRequired,
    isLoading: PropTypes.bool,
    error: PropTypes.object,
};
